<template>
  <q-toolbar elevated class="toolbar" :style="{ backgroundColor: navBarColor }">
    <div class="logo-and-path">
      <q-btn
        :ripple="false"
        :stretch="false"
        fab-mini
        dense
        flat
        to="/"
        class="logo-btn"
        data-dd-action-name="Horizontal NavBar: open home page action"
      >
        <img src="@/assets/images/Iris-logo_coloured.png" alt="iris_typography_logo" class="logo-img" />
      </q-btn>

      <HolidaysBanner v-once v-if="isNowHolidaySeason" />
    </div>

    <div class="text-bold path">
      {{ viewTitle }}
    </div>

    <q-space />
    <SearchInput v-if="searchInputVisibility" />

    <q-btn-dropdown
      flat
      auto-close
      icon="add"
      color="accent"
      class="q-ml-sm"
      label="Create"
      data-dd-action-name="Horizontal NavBar: CREATE action"
    >
      <q-list dense class="text-accent" :style="{ backgroundColor: navBarColor }">
        <q-item
          clickable
          v-close-popup
          @click="showNewRuleDialogue = true"
          data-dd-action-name="Horizontal NavBar: create rule action"
        >
          <q-item-section>
            <q-item-label>Rule</q-item-label>
          </q-item-section>
        </q-item>

        <q-item
          clickable
          v-close-popup
          @click="showNewPolicyDialogue = true"
          data-dd-action-name="Horizontal NavBar: create policy action"
        >
          <q-item-section>
            <q-item-label>Policy</q-item-label>
          </q-item-section>
        </q-item>

        <q-item
          clickable
          v-close-popup
          @click="showNewUserFunctionDialogue = true"
          data-dd-action-name="Horizontal NavBar: create function action"
        >
          <q-item-section>
            <q-item-label>User function</q-item-label>
          </q-item-section>
        </q-item>
      </q-list>
    </q-btn-dropdown>

    <div class="expiry-time q-pl-md" v-if="minutesTilAuthExpiration < 10">
      <span v-if="minutesTilAuthExpiration > 0"
        >Auth expires in {{ minutesTilAuthExpiration }} minute<span v-if="minutesTilAuthExpiration > 1">s</span>
      </span>
      <span v-if="minutesTilAuthExpiration === 0"> Auth expires in {{ secondsTilAuthExpiration }} seconds</span>
      <span v-if="secondsTilAuthExpiration <= 0"> Auth expired </span>
      <q-tooltip>Once expired you can still browse around, but any write operation will redirect to login.</q-tooltip>
    </div>

    <q-btn-dropdown
      flat
      bordered
      type="secondary"
      class="q-mx-md"
      data-dd-action-name="Horizontal NavBar: expand profile action"
    >
      <template #label>
        <div class="row items-center no-wrap">
          <q-icon left name="person" class="flex" />
          <div class="text-center">
            {{ firstName }}
          </div>
          <div class="admin" v-if="isAdminTeam">
            <q-badge color="accent">Admin</q-badge>
            <q-tooltip>
              You have been assigned to an admin role. Note that you have access to extra functionalities and features.
            </q-tooltip>
          </div>
        </div>
      </template>
      <q-list class="text-accent" :style="{ backgroundColor: navBarColor }">
        <q-item clickable v-close-popup :to="{ name: 'logout' }" data-dd-action-name="Horizontal NavBar: logout action">
          <q-item-section>
            <q-item-label>Logout</q-item-label>
          </q-item-section>
        </q-item>
      </q-list>
    </q-btn-dropdown>

    <NewPolicyDialogue :visible="showNewPolicyDialogue" @close="closeNewPolicyDialogue" />

    <NewRuleDialogue :visible="showNewRuleDialogue" @close="closeNewRuleDialogue" />

    <NewUserFunctionDialogue :visible="showNewUserFunctionDialogue" @close="closeNewUserFunctionDialogue" />
  </q-toolbar>
</template>

<script lang="ts">
import NewPolicyDialogue from '@/components/common/NewPolicyDialogue.vue'
import NewRuleDialogue from '@/components/common/NewRuleDialogue.vue'
import NewUserFunctionDialogue from '@/components/common/NewUserFunctionDialogue.vue'
import SearchInput from '@/components/common/SearchInput.vue'
import { MAIN_NAVBAR_COLOR } from '@/config'
import { getViewTitle } from '@/router'
import type { User } from '@/types'
import { HOLIDAY_SEASON_GREETINGS_TITLE, isHolidaySeason } from '@/utils/periods'
import { useDirectStore } from '@/utils/utils'
import { computed, defineComponent, onMounted, onUnmounted, ref } from 'vue'
import { useRoute } from 'vue-router/composables'
import HolidaysBanner from './HolidaysBanner.vue'

interface Props {
  user?: User
  authExpiresAt?: Date
}

export default defineComponent<Props>({
  components: {
    NewRuleDialogue,
    NewPolicyDialogue,
    NewUserFunctionDialogue,
    SearchInput,
    HolidaysBanner,
  },

  props: {
    user: {
      type: Object,
      default: undefined,
    },
    authExpiresAt: {
      type: Date,
      default: undefined,
    },
  },

  setup(props) {
    const { getters } = useDirectStore()
    const route = useRoute()

    const firstName = computed(() => {
      const { email } = props.user || { email: '.' }
      return email.split('.')[0]
    })
    const routePath = computed(() => route.path)
    const isAdminTeam = computed(() => getters.auth.isAdminTeam)

    const showNewPolicyDialogue = ref(false)
    const showNewRuleDialogue = ref(false)
    const showNewUserFunctionDialogue = ref(false)

    function closeNewPolicyDialogue() {
      showNewPolicyDialogue.value = false
    }

    function closeNewRuleDialogue() {
      showNewRuleDialogue.value = false
    }

    function closeNewUserFunctionDialogue() {
      showNewUserFunctionDialogue.value = false
    }

    const searchInputVisibility = computed(() => routePath.value && routePath.value !== '/search')

    const currentTime = ref(new Date())
    let timeIntervalHandle: number

    onMounted(() => {
      timeIntervalHandle = window.setInterval(() => {
        currentTime.value = new Date()
      }, 3000)
    })

    onUnmounted(() => {
      clearInterval(timeIntervalHandle)
    })

    const isNowHolidaySeason = computed(() => isHolidaySeason())

    return {
      navBarColor: MAIN_NAVBAR_COLOR,
      isAdminTeam,
      isNowHolidaySeason,
      closeNewPolicyDialogue,
      closeNewRuleDialogue,
      closeNewUserFunctionDialogue,
      showNewPolicyDialogue,
      showNewRuleDialogue,
      showNewUserFunctionDialogue,
      viewTitle: computed(() =>
        isNowHolidaySeason.value ? HOLIDAY_SEASON_GREETINGS_TITLE : getViewTitle(route.name!)
      ),
      closeWindow() {
        window.close()
      },
      firstName,
      searchInputVisibility,
      minutesTilAuthExpiration: computed(() => {
        if (!props.authExpiresAt) {
          return undefined
        }
        return Math.floor((props.authExpiresAt.getTime() - currentTime.value.getTime()) / 1000 / 60)
      }),
      secondsTilAuthExpiration: computed(() => {
        if (!props.authExpiresAt) {
          return undefined
        }
        return Math.floor((props.authExpiresAt.getTime() - currentTime.value.getTime()) / 1000)
      }),
    }
  },
})
</script>
<style lang="scss" scoped>
@import '@/styles/quasar.variables.scss';

.toolbar {
  z-index: 9999;
  padding: $space-x-sm 0;
  max-height: 52px;
  color: $accent;
  float: left;
  font-size: 1em;
}

.nonprod-colors {
  background-color: #c0871f;
}

.production-colors {
  background-color: $primary;
}

.logo-and-path {
  display: flex;
  align-items: center;
}

.logo-btn {
  margin: $space-x-sm;
  max-height: 100%;
}

.logo-img {
  width: auto;
  max-height: 1.7em;
}

.path {
  margin: $space-x-sm;
  text-align: center;
}

.user-btn__popup {
  background-color: $accent;
  color: $primary;
}

.icons {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
}

.avatars {
  display: flex;
  flex-direction: column;
}

.title {
  text-align: center;
  font-family: 'Damion', cursive;
  font-weight: 500;
  font-size: 3rem;
  line-height: 1;
  color: $background;
  margin-left: $space-x-lg;
  margin-bottom: $space-y-md;
}

.avatars-typo {
  font-size: 0.7rem;
  font-weight: 700;
}

.iris-logo {
  width: 1em;
  height: auto;
}

.logout {
  font-variant: small-caps;
  font-weight: 700;
}

.glowingCircle {
  position: absolute;
  width: 80%;
  height: 80%;
  top: 10%;
  right: 10%;
  left: 10%;
  bottom: 10%;
  border-radius: 50%;
  background-color: white;
  animation-name: glow;
  animation-duration: 0.8s;
  animation-timing-function: ease-in-out;
  animation-delay: 1s;
  animation-iteration-count: infinite;
}

@keyframes glow {
  from {
    transform: scale(1);
    opacity: 0.7;
  }
  to {
    transform: scale(2.5);
    opacity: 0;
  }
}

.logo-and-buttons {
  display: flex;
  align-items: center;
}

.admin {
  color: $accent;
  padding: 0 $space-x-sm;
}
</style>
